import type { PackageResponse } from '../../types/packages';
import { formatAed } from '../offers/offerReviewModel';
import type { PackageEditErrors, PackageEditForm } from './packageEditModel';
import { PERCENT_MAX, PERCENT_MIN } from './packageEditModel';

export interface PackageEditPricingProps {
  pkg: PackageResponse;
  form: PackageEditForm;
  errors: PackageEditErrors;
  disabled: boolean;
  onChange: (key: 'packagePrice' | 'discountPercent', value: string) => void;
}

/** The mode is derived from the items (D2), so the admin edits only the price (FIXED) or the percent (PERCENT). */
export default function PackageEditPricing(props: PackageEditPricingProps) {
  const { pkg, form, errors, disabled, onChange } = props;
  const fixed = pkg.pricingMode === 'FIXED';
  const key = fixed ? 'packagePrice' : 'discountPercent';
  const typed = Number(form[key]);
  const saving = fixed && pkg.originalTotal != null && form.packagePrice.trim() !== '' && Number.isFinite(typed)
    ? pkg.originalTotal - typed
    : null;

  return (
    <section className="admin-card" aria-labelledby="package-edit-pricing">
      <div className="admin-card-header">
        <h3 className="admin-card-title" id="package-edit-pricing">Price</h3>
        <span className="admin-badge neutral">{fixed ? 'Fixed price' : 'Percent off'}</span>
      </div>
      <p className="offer-edit__hint">
        {fixed
          ? `Original total ${formatAed(pkg.originalTotal)} (every choice is fixed).`
          : `Lowest total ${formatAed(pkg.fromTotal ?? pkg.originalTotal)}; customers pick the open choices.`}
      </p>
      <div className="admin-form-group">
        <label className="admin-form-label" htmlFor={`package-edit-${key}`}>{fixed ? 'Package price (AED)' : 'Discount (%)'}</label>
        <input
          id={`package-edit-${key}`}
          className="admin-form-input"
          type="number"
          inputMode="decimal"
          step={fixed ? '0.01' : 1}
          min={fixed ? 1 : PERCENT_MIN}
          max={fixed ? undefined : PERCENT_MAX}
          value={form[key]}
          disabled={disabled}
          onChange={(e) => onChange(key, e.target.value)}
          {...(errors[key] ? { 'aria-invalid': true as const, 'aria-describedby': `package-edit-err-${key}` } : {})}
        />
        {saving != null && saving > 0 && pkg.originalTotal ? (
          <p className="offer-edit__hint" data-testid="package-edit-saving">
            Customer saves {formatAed(saving)} ({Math.floor((saving / pkg.originalTotal) * 100)}%).
          </p>
        ) : null}
        {errors[key] && <div className="offer-edit__error" id={`package-edit-err-${key}`} role="alert">{errors[key]}</div>}
      </div>
    </section>
  );
}
